import React from 'react';
import {
    Users,
    School,
    Clock,
    Activity,
    TrendingUp,
    Download,
    LayoutDashboard
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Loader } from '../../components/ui/Loader';
import { pdfService } from '../../services';
import { useSchools, useRegistrationStatistics, useActiveEvents, useRecentLogs } from '../../hooks/useConvex';

export const DashboardPage: React.FC = () => {
    // Convex queries
    const schools = useSchools();
    const registrationStats = useRegistrationStatistics();
    const activeEvents = useActiveEvents();
    const recentLogs = useRecentLogs(5);

    const loading = schools === undefined || registrationStats === undefined || activeEvents === undefined;

    const stats = {
        totalSchools: schools?.length ?? 0,
        totalStudents: registrationStats?.totalParticipants ?? 0,
        activeEvents: activeEvents?.length ?? 0,
        pendingRegistrations: registrationStats?.submitted ?? 0,
        approvedRegistrations: registrationStats?.approved ?? 0,
        totalRegistrations: registrationStats?.total ?? 0
    };

    const handleExportPDF = () => {
        const data = [
            ['Total Schools', stats.totalSchools],
            ['Total Students', stats.totalStudents],
            ['Active Events', stats.activeEvents],
            ['Total Registrations', stats.totalRegistrations],
            ['Pending (Submitted)', stats.pendingRegistrations],
            ['Approved', stats.approvedRegistrations],
        ];

        pdfService.generatePDF({
            title: 'Dashboard Overview',
            filename: 'dashboard_overview',
            columns: ['Metric', 'Value'],
            data: data,
        });
    };

    if (loading) return <Loader fullScreen />;

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center">
                            <LayoutDashboard className="w-5 h-5 text-brand" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white tracking-tight">Command Center</h1>
                            <p className="text-sm text-white/30 mt-0.5">Real-time system overview</p>
                        </div>
                    </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleExportPDF}>
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                </Button>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Schools */}
                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6 relative overflow-hidden group hover:border-white/[0.1] transition-all duration-300">
                    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#3B82F6]/40 to-transparent" />
                    <div className="w-9 h-9 rounded-lg bg-[#3B82F6]/[0.08] border border-[#3B82F6]/[0.12] flex items-center justify-center mb-4">
                        <School className="w-4.5 h-4.5 text-[#3B82F6]" />
                    </div>
                    <div className="text-3xl font-mono font-bold text-white tracking-tight mb-1">{stats.totalSchools}</div>
                    <div className="text-xs text-white/30 uppercase tracking-wider">Schools</div>
                </div>

                {/* Students */}
                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6 relative overflow-hidden group hover:border-white/[0.1] transition-all duration-300">
                    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#F59E0B]/40 to-transparent" />
                    <div className="w-9 h-9 rounded-lg bg-[#F59E0B]/[0.08] border border-[#F59E0B]/[0.12] flex items-center justify-center mb-4">
                        <Users className="w-4.5 h-4.5 text-[#F59E0B]" />
                    </div>
                    <div className="text-3xl font-mono font-bold text-white tracking-tight mb-1">{stats.totalStudents}</div>
                    <div className="text-xs text-white/30 uppercase tracking-wider">Students</div>
                </div>

                {/* Active Events */}
                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6 relative overflow-hidden group hover:border-white/[0.1] transition-all duration-300">
                    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#10B981]/40 to-transparent" />
                    <div className="w-9 h-9 rounded-lg bg-[#10B981]/[0.08] border border-[#10B981]/[0.12] flex items-center justify-center mb-4">
                        <Activity className="w-4.5 h-4.5 text-[#10B981]" />
                    </div>
                    <div className="text-3xl font-mono font-bold text-white tracking-tight mb-1">{stats.activeEvents}</div>
                    <div className="text-xs text-white/30 uppercase tracking-wider">Active Events</div>
                </div>

                {/* Pending Registrations */}
                <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6 relative overflow-hidden group hover:border-white/[0.1] transition-all duration-300">
                    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#EF4444]/40 to-transparent" />
                    <div className="w-9 h-9 rounded-lg bg-[#EF4444]/[0.08] border border-[#EF4444]/[0.12] flex items-center justify-center mb-4">
                        <Clock className="w-4.5 h-4.5 text-[#EF4444]" />
                    </div>
                    <div className="text-3xl font-mono font-bold text-white tracking-tight mb-1">{stats.pendingRegistrations}</div>
                    <div className="text-xs text-white/30 uppercase tracking-wider">Pending Regs</div>
                </div>
            </div>

            {/* Two-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Left: Activity Feed */}
                <div className="lg:col-span-3 bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/[0.04] flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4 text-brand" />
                            <h3 className="text-sm font-semibold text-white/70">Live Activity</h3>
                        </div>
                        <button className="text-xs text-brand hover:text-brand-light transition-colors">
                            View All
                        </button>
                    </div>
                    <div className="divide-y divide-white/[0.03]">
                        {recentLogs && recentLogs.length > 0 ? (
                            recentLogs.map((log) => {
                                const borderColor = log.action.includes('create')
                                    ? 'border-l-[#10B981]'
                                    : log.action.includes('delete')
                                    ? 'border-l-[#EF4444]'
                                    : 'border-l-[#3B82F6]';

                                return (
                                    <div
                                        key={log._id}
                                        className={`px-6 py-3.5 border-l-[3px] ${borderColor} hover:bg-white/[0.02] transition-colors duration-150`}
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-white/70 truncate">
                                                    {log.details || log.action}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {log.user_email && (
                                                    <span className="bg-white/[0.04] px-2 py-0.5 rounded-full text-[10px] text-white/30">
                                                        {log.user_email}
                                                    </span>
                                                )}
                                                <span className="text-[11px] font-mono text-white/20">
                                                    {new Date(log.timestamp).toLocaleTimeString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="px-6 py-12 text-center">
                                <Activity className="w-8 h-8 text-white/10 mx-auto mb-2" />
                                <p className="text-sm text-white/30">No recent activity</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: Registration Overview */}
                <div className="lg:col-span-2 bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/[0.04] flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-brand" />
                            <h3 className="text-sm font-semibold text-white/70">Registrations</h3>
                        </div>
                    </div>
                    <div className="p-6 space-y-6">
                        {/* Pending */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-white/40 uppercase tracking-wider">Pending</span>
                                <span className="text-sm font-mono font-semibold text-brand">{stats.pendingRegistrations}</span>
                            </div>
                            <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
                                <div
                                    className="h-full bg-brand rounded-full transition-all duration-500"
                                    style={{ width: `${stats.totalRegistrations > 0 ? (stats.pendingRegistrations / stats.totalRegistrations * 100) : 0}%` }}
                                />
                            </div>
                            <div className="text-right mt-1">
                                <span className="text-[10px] font-mono text-white/20">
                                    {stats.totalRegistrations > 0 ? Math.round((stats.pendingRegistrations / stats.totalRegistrations * 100)) : 0}%
                                </span>
                            </div>
                        </div>

                        {/* Approved */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-white/40 uppercase tracking-wider">Approved</span>
                                <span className="text-sm font-mono font-semibold text-[#10B981]">{stats.approvedRegistrations}</span>
                            </div>
                            <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
                                <div
                                    className="h-full bg-[#10B981] rounded-full transition-all duration-500"
                                    style={{ width: `${stats.totalRegistrations > 0 ? (stats.approvedRegistrations / stats.totalRegistrations * 100) : 0}%` }}
                                />
                            </div>
                            <div className="text-right mt-1">
                                <span className="text-[10px] font-mono text-white/20">
                                    {stats.totalRegistrations > 0 ? Math.round((stats.approvedRegistrations / stats.totalRegistrations * 100)) : 0}%
                                </span>
                            </div>
                        </div>

                        {/* Total */}
                        <div className="pt-4 border-t border-white/[0.06]">
                            <div className="text-center">
                                <div className="text-4xl font-mono font-bold text-white mb-1">{stats.totalRegistrations}</div>
                                <div className="text-[10px] text-white/30 uppercase tracking-widest">Total Registrations</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
